import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { plans, site } from "@/config/site";

const WhatsAppGlyph = () => null;

const rows = [
  { label: "Price", vals: plans.map((p) => `₹${p.price.toLocaleString("en-IN")} /PC/mo`), highlight: true },
  { label: "Storage", vals: plans.map((p) => p.storage) },
  { label: "File history", vals: plans.map((p) => p.history) },
  { label: "Restore on request", vals: [true, true, true, true, true] },
  { label: "Managed setup", vals: [true, true, true, true, true] },
  { label: "Support", vals: plans.map((p) => p.support) },
];

const Cell = ({ v }) => {
  if (v === true) return <Check className="h-5 w-5 text-brand mx-auto" strokeWidth={2.4} />;
  if (v === false) return <X className="h-5 w-5 text-hairline mx-auto" strokeWidth={2} />;
  return <span className="text-ink">{v}</span>;
};

export default function Pricing() {
  return (
    <>
      {/* HERO */}
      <section
        className="text-white"
        style={{ background: "linear-gradient(105deg,#0F2B46 0%,#196CA3 100%)" }}
      >
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="rule" style={{ background: "#4FC3F7" }} />
          <div className="eyebrow !text-brand-light">Pricing</div>
          <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold leading-tight tracking-tight">
            Simple, honest pricing.
          </h1>
          <p className="mt-4 text-lg text-brand-mist max-w-2xl">
            Per computer, per month. Billed annually. No hidden fees. No setup charge.
          </p>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`relative rounded-card p-5 transition-all ${
                  p.highlight
                    ? "bg-white border-2 border-brand shadow-card-lift lg:-translate-y-3"
                    : "bg-brand-card"
                }`}
                data-testid={`plan-card-${p.id}`}
              >
                {p.badge && (
                  <span className="absolute -top-3 left-4 rounded-full bg-brand text-white text-[10px] font-bold px-2.5 py-1 uppercase tracking-wider">
                    {p.badge}
                  </span>
                )}
                <h3 className="text-xl font-bold text-ink">{p.name}</h3>
                <p className="mt-1 text-xs text-ink-body">{p.tagline}</p>
                <div className="mt-5">
                  <span className="text-3xl font-extrabold text-ink">
                    ₹{p.price.toLocaleString("en-IN")}
                  </span>
                  <span className="text-xs ml-1 text-ink-body">/PC/mo</span>
                </div>
                <ul className="mt-5 space-y-2.5 text-sm text-ink-body">
                  {[
                    `${p.storage} storage`,
                    `${p.history} file history`,
                    "Restore on request",
                    "Managed setup",
                    `${p.support} support`,
                  ].map((line, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-brand" strokeWidth={2.4} />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/get-started?plan=${p.id}`}
                  className={`mt-6 inline-flex items-center justify-center gap-2 w-full rounded-md px-4 py-2.5 text-sm font-semibold transition-all ${
                    p.highlight
                      ? "text-white shadow-card-soft hover:shadow-card-lift"
                      : "border border-brand text-brand hover:bg-brand hover:text-white"
                  }`}
                  style={
                    p.highlight
                      ? { background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }
                      : {}
                  }
                  data-testid={`plan-cta-${p.id}`}
                >
                  Get Started
                  <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section style={{ backgroundColor: "#F8FAFC" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16">
          <div className="rule" />
          <div className="eyebrow">Compare</div>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-ink mb-8">
            All plans, side by side.
          </h2>
          <div className="overflow-x-auto rounded-card bg-white border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline">
                  <th className="text-left px-5 py-4 font-semibold text-ink w-1/4">Feature</th>
                  {plans.map((p) => (
                    <th
                      key={p.id}
                      className={`px-5 py-4 text-base font-bold ${
                        p.highlight ? "text-brand" : "text-ink"
                      }`}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={i}
                    className={`border-b border-hairline last:border-b-0 ${
                      r.highlight ? "bg-brand-card/40" : ""
                    }`}
                  >
                    <td className="px-5 py-4 font-medium text-ink-body">{r.label}</td>
                    {r.vals.map((v, j) => (
                      <td key={j} className="px-5 py-4 text-center text-ink-body">
                        <Cell v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* NOTES */}
      <section className="bg-white">
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                t: "What is \"file history\"?",
                b: "If you deleted or changed a file, we can go back and get an older version — up to this many days back.",
              },
              {
                t: "All plans billed annually",
                b: "Monthly billing available on request at a small premium.",
              },
              {
                t: "GST extra",
                b: "As applicable, added to your invoice.",
              },
              {
                t: "Bigger than 500 GB or 10+ computers?",
                b: (
                  <>
                    <a
                      href={`mailto:${site.email}?subject=VaultSafe%20—%2010%2B%20PCs%20enquiry`}
                      className="text-brand underline hover:no-underline font-medium"
                    >
                      Email us
                    </a>{" "}
                    — we'll work something out.
                  </>
                ),
              },
            ].map((n, i) => (
              <div key={i} className="rounded-card bg-brand-card p-5">
                <h3 className="text-base font-semibold text-ink">{n.t}</h3>
                <p className="mt-2 text-sm text-ink-body leading-relaxed">{n.b}</p>
              </div>
            ))}
          </div>

          <div
            className="mt-6 rounded-card p-6"
            style={{ background: "#E8F4EA", borderLeft: "4px solid #2C994D" }}
          >
            <h3 className="text-base font-semibold text-leaf-dark">
              A note on what VaultSafe is not
            </h3>
            <p className="mt-2 text-sm text-ink-body leading-relaxed max-w-3xl">
              VaultSafe backs up your <strong>files and folders</strong> — documents,
              Tally data, drawings, records, photos. It does <strong>not</strong> create
              a full image of your computer, so it can't reinstall Windows for you. If
              your machine dies, we get your data back onto the new one; the operating
              system and programs are reinstalled the usual way.
            </p>
          </div>
        </div>
      </section>

      {/* GREEN CTA BAND */}
      <section style={{ background: "linear-gradient(100deg,#1D6C35 0%,#2C994D 100%)" }}>
        <div className="mx-auto max-w-site px-5 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl">
                Ready to protect your business?
              </h2>
              <p className="mt-3 text-white/85 max-w-xl">
                Fill in a two-minute form. We'll match you to the right plan.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/get-started"
                className="inline-flex items-center gap-2 rounded-md bg-white text-leaf-dark px-6 py-3.5 font-bold text-base shadow-card-lift hover:-translate-y-px transition-all"
                data-testid="pricing-final-cta"
              >
                Start with a Free Trial
                <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
